import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  SvgIcon,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import GetAppIcon from '@material-ui/icons/GetApp';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { DatePickerRangeComponent } from 'src/components';
import { dateToday, downloadCSV, formatDate } from 'src/utils';
import { BranchMeta } from 'src/types';
import { multiBranchFeat } from 'src/constants/feature-toggle';
import { CategoryListDropdown } from 'src/components/filters/CategoryDropdown';
import { BranchListDropDown } from 'src/components/dropdown';
import { useSearchParams } from 'react-router-dom';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { isEmpty } from 'lodash';
import { ManufacturerDropDownV2 } from 'src/components/dropdown/ManufacturerDropdownV2';
import { useSnackBar } from 'src/hooks';
import { unwrapResult } from '@reduxjs/toolkit';

const { actions: inventoryActions } = slices.inventory;
const { selectors: categorySelectors } = slices.category;

interface Props {
  keyword?: string;
  fromDate?: string;
  toDate?: string;
  onChangeFromDate?: (date?: string) => void;
  onChangeToDate?: (date?: string) => void;
  onApplyFilterPress: () => void;
  onHandleCategoryChange: (category_id?: number) => void;
  onChangeKeyword: (keyword?: string) => void;
  onHandleExcludedManufacturer: (manufacturer?: number[]) => void;
  className?: string;
  setSelectedBranchIDs: (v: number[]) => void;
  isLoading: boolean;
  userBranchOptions: BranchMeta[];
  selectedBranchIDs: number[];
}

const useStyles = makeStyles((theme) => ({
  root: {},
  filterTitle: {
    marginBottom: theme.spacing(1)
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  importButton: {
    marginRight: theme.spacing(1)
  },
  exportButton: {
    marginRight: theme.spacing(1)
  },
  cardContent: {
    flexDirection: 'row'
  },
  datePickersContainer: {
    marginTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  dashSeparator: {
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1)
  },
  buttonsContainer: {
    marginTop: theme.spacing(1)
  },
  chip: {
    marginRight: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5)
  }
}));

const AvailableInventoryFilter = ({
  keyword,
  className,
  onApplyFilterPress,
  fromDate,
  toDate,
  onChangeFromDate,
  onChangeToDate,
  setSelectedBranchIDs,
  onHandleCategoryChange,
  onHandleExcludedManufacturer,
  onChangeKeyword,
  isLoading,
  selectedBranchIDs,
  ...rest
}: Props) => {
  const classes = useStyles();
  const snackBar = useSnackBar();
  const dispatch = useAppDispatch();

  const [searchParams] = useSearchParams();
  const categories = useAppSelector(categorySelectors.selectCategories);

  const queryParams = useMemo(() => {
    return Object.fromEntries(searchParams.entries());
  }, [searchParams]);

  const init_category = useMemo(
    () =>
      categories?.find(
        (categ) => categ?.id?.toString() === queryParams?.category_id
      ),
    [queryParams, categories]
  );

  const init_excluded_manufacturers = useMemo(() => {
    const excluded_manufacturer = queryParams.excluded_manufacturer_ids;
    if (!excluded_manufacturer) return [];
    return decodeURIComponent(excluded_manufacturer)
      .split(',')
      .map(Number)
      .filter(Boolean);
  }, [queryParams]);

  const onInternalApply = useCallback(() => {
    onApplyFilterPress();
  }, [onApplyFilterPress]);

  const transformerDateField = (date: MaterialUiPickersDate) => {
    const dateUtc = formatDate(date || '', 'yyyy-MM-dd');
    return dateUtc;
  };

  const onChangedDate = useCallback(
    (date: MaterialUiPickersDate, field: 'from' | 'to') => {
      const transformedDate = date ? transformerDateField(date) : undefined;

      if (onChangeToDate && field === 'to') {
        onChangeToDate(transformedDate);
      }
      if (onChangeFromDate && field === 'from') {
        onChangeFromDate(transformedDate);
      }
    },
    [onChangeFromDate, onChangeToDate]
  );

  const onDownloadNvidiaAvailableInventory = async () => {
    try {
      const res = unwrapResult(
        await dispatch(
          inventoryActions.getAvailableInventoryNvidiaThunk({
            start_date: fromDate,
            end_date: toDate
          })
        )
      );

      snackBar.show({
        message: 'Download started',
        severity: 'success'
      });

      if (res?.success) {
        const csvName = `Nvidia Available Inventory From ${formatDate(
          fromDate
        )} to ${formatDate(toDate)}.csv`;
        const structured_list = res?.originalData?.data?.map(
          (inventory) => inventory
        );

        downloadCSV(structured_list, csvName);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const RenderButtonsGroup = () => (
    <Grid
      container
      spacing={1}
      alignItems="center"
      justifyContent="space-between"
    >
      <Grid item className={classes.buttonsContainer}>
        <Button
          endIcon={<GetAppIcon />}
          onClick={onDownloadNvidiaAvailableInventory}
          color="primary"
          variant="text"
          size="small"
          disabled={isLoading}
        >
          Nvidia available inventory
        </Button>
      </Grid>

      <Grid item className={classes.buttonsContainer}>
        <Button
          endIcon={<SearchIcon />}
          onClick={onInternalApply}
          color="primary"
          variant="contained"
          disabled={isLoading}
        >
          Apply Filter
        </Button>
      </Grid>
    </Grid>
  );

  const RenderDatePickersFilter = () => (
    <Grid container spacing={4} alignItems="center" justify="center">
      <Grid item>
        <DatePickerRangeComponent
          title="Date Availability"
          fromDateValue={fromDate || queryParams?.start_date || null}
          toDateValue={toDate || queryParams?.end_date || null}
          dateToLabel="End Date"
          dateFromLabel="Start Date"
          toDateMin={fromDate}
          fromDateMax={dateToday()}
          onChangeToDate={(date) => onChangedDate(date, 'to')}
          onChangeFromDate={(date) => onChangedDate(date, 'from')}
        />
      </Grid>
    </Grid>
  );

  const RenderBranchSelection = useCallback(() => {
    return (
      <>
        {multiBranchFeat ? (
          <Grid container spacing={4} alignItems="center" justify="center">
            <Grid item xs={12} md={4}>
              <BranchListDropDown
                defaultValue={
                  selectedBranchIDs && !isEmpty(selectedBranchIDs)
                    ? selectedBranchIDs
                    : queryParams?.branch_ids?.split(',').map(Number)
                }
                isElevated={false}
                paperSpace={0}
                multiple
                onHandleBranchChange={(branchIds?: number[]) => {
                  if (branchIds) {
                    setSelectedBranchIDs(branchIds);
                  }
                }}
              />
            </Grid>
          </Grid>
        ) : null}
      </>
    );
  }, [queryParams.branch_ids, selectedBranchIDs, setSelectedBranchIDs]);

  const RenderCategoryList = useCallback(() => {
    return (
      <Grid container spacing={4} alignItems="center" justify="center">
        <Grid item xs={12} md={4}>
          <CategoryListDropdown
            defaultValue={init_category}
            onHandleCategories={(categoryId: number) =>
              onHandleCategoryChange(categoryId)
            }
          />
        </Grid>
      </Grid>
    );
  }, [init_category, onHandleCategoryChange]);

  const RenderExcludedManufacturer = useCallback(() => {
    return (
      <Grid container spacing={4} alignItems="center" justify="center">
        <Grid item xs={12} md={4}>
          <ManufacturerDropDownV2
            initialValue={init_excluded_manufacturers}
            isMultiple={true}
            label="Excluded Manufacturers(optional)"
            onHandleManufacturerSetValue={(manufacturer_ids?: number[]) => {
              onHandleExcludedManufacturer(manufacturer_ids);
            }}
          />
        </Grid>
      </Grid>
    );
  }, [init_excluded_manufacturers, onHandleExcludedManufacturer]);

  const RenderSearchKeyword = useCallback(() => {
    return (
      <Grid container spacing={4} alignItems="center" justify="center">
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Search Keyword"
            variant="outlined"
            value={keyword || ''}
            onChange={(e) => onChangeKeyword(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SvgIcon fontSize="small" color="action">
                    <SearchIcon />
                  </SvgIcon>
                </InputAdornment>
              )
            }}
          />
        </Grid>
      </Grid>
    );
  }, [keyword, onChangeKeyword]);

  return (
    <div className={clsx(classes.root, className)} {...rest}>
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Typography color="textPrimary" gutterBottom variant="h3">
          Available Inventory
        </Typography>
      </Box>
      <Box mt={3}>
        <Card>
          <CardContent>
            {RenderDatePickersFilter()}
            {RenderSearchKeyword()}
            {RenderCategoryList()}
            {RenderBranchSelection()}
            {RenderExcludedManufacturer()}
            {RenderButtonsGroup()}
          </CardContent>
        </Card>
      </Box>
    </div>
  );
};

export default AvailableInventoryFilter;
